import { api } from "@/lib/api"
import { PaymentStatus, type PaymentGateway } from "@/types/form-types"
import { logger } from "@/utils/logger"

interface RazorpayOptions {
	key: string
	amount: number
	currency: string
	name: string
	description: string
	order_id: string
	handler: (response: any) => void
	modal: {
		ondismiss: () => void
	}
	prefill: { name: string; email: string; contact: string }
	theme: { color: string; backdrop_color: string; hide_topbar: boolean }
}

interface RazorpayInstance {
	on: (event: string, callback: (response: any) => void) => void
	open: () => void
	close: () => void
}

interface RazorpayWindow extends Window {
	Razorpay: new (options: RazorpayOptions) => RazorpayInstance
}

declare const window: RazorpayWindow

const loadRazorpay = () =>
	new Promise<void>((resolve, reject) => {
		if (document.getElementById("razorpay-sdk")) {
			resolve()
			return
		}
		const script = document.createElement("script")
		script.id = "razorpay-sdk"
		script.src = "https://checkout.razorpay.com/v1/checkout.js"
		script.onload = () => resolve()
		script.onerror = () => reject("Failed to load Razorpay SDK")
		document.body.appendChild(script)
	})

export const RazorpayGateway: PaymentGateway = {
	async initPayment({
		orderId,
		transactionId,
		amount,
		currency,
		applicationId,
		gatewayId,
		setValues,
		mode,
		batchId,
		partnerId,
		key,
		paymentMeta,
		setShowPaymentGateway,
		showPaymentGatewayRef,
	}) {
		await loadRazorpay()

		const options: RazorpayOptions = {
			key: key,
			amount,
			currency,
			name: paymentMeta?.university_name || "University",
			description: "Payment for course",
			order_id: orderId,
			handler: async (response: any) => {
				logger.info("Transaction handler called")

				const payload = {
					notes: {
						application_id: applicationId,
						transaction_id: transactionId,
					},
					razorpay_order_id: response.razorpay_order_id,
					razorpay_signature: response.razorpay_signature,
					razorpay_payment_id: response.razorpay_payment_id,
				}
				// verify payment on backend
				const res = await api.post(
					`/orders/${orderId}/transactions/${transactionId}/verify?gateway=${gatewayId}`,
					payload,
					{
						headers: {
							mode: mode,
							"X-Batch-ID": batchId,
							"X-Partner-ID": partnerId,
						},
					},
				)
				if (!res || res.status !== 200 || res.data === false) {
					setValues({
						payment_status: PaymentStatus.SERVICE_UNAVAILABLE,
					})
				}
			},
			modal: {
				ondismiss: function () {
					logger.info("User closed the popup / payment is failed")
					setShowPaymentGateway(true)
					showPaymentGatewayRef.current = true
					setValues({
						payment_status:
							PaymentStatus.PAYMENT_FAILED_RETRY_ALLOWED,
					})
				},
			},
			prefill: {
				name: paymentMeta?.full_name || "John Doe",
				email: paymentMeta?.email || "john@example.com",
				contact: paymentMeta?.phone || "9876543210",
			},
			theme: {
				color: "#F3F4F6",
				backdrop_color: "#F3F4F6",
				hide_topbar: true,
			},
		}

		const rzp = new window.Razorpay(options)
		rzp.open()
	},
}
